/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DnsModule
 */

import {
    IDnsTransportProtocolMatch,
    MatchOperation,
} from 'generated-types';

import { DnsTransportProtocolMatch } from 'object-types';

import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';
import { TEnumValueLabelsHash } from 'ajs/modules/core/services/schema-service/schema.types';

/**
 * @description  DnsTransportProtocolMatch ConfigItem class.
 * @author Abhinesh Gour
 */
export class DnsTransportProtocolMatchConfigItem extends MessageItem<IDnsTransportProtocolMatch> {
    public static ajsDependencies = [
        'schemaService',
    ];

    private readonly schemaService: SchemaService;
    private readonly matchValueLabelsHash: TEnumValueLabelsHash;

    constructor(args = {}) {
        super({
            objectType: DnsTransportProtocolMatch,
            ...args,
        });

        this.schemaService = this.getAjsDependency_('schemaService');

        /**
         * Labels representing match values.
         */
        this.matchValueLabelsHash =
            this.schemaService.getEnumValueLabelsHash('DnsTransportProtocol');
    }

    /**
     * Convenience method to get text label for match criteria.
     */
    public get matchCriteria(): string {
        return this.schemaService.getEnumValueLabel(
            'MatchOperation',
            this.config.match_criteria,
        );
    }

    /**
     * Value of match to represent the data. Used as part of expander info.
     */
    public get matchValue(): string {
        const { protocol: protocolEnumValue } = this.config;
        const value = this.matchValueLabelsHash[protocolEnumValue];

        return value;
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IDnsTransportProtocolMatch> {
        const defaultValues = this.getAjsDependency_('defaultValues');
        const defaultConfig = defaultValues.getDefaultItemConfigByType(this.objectType) || {};

        return {
            ...defaultConfig,
            match_criteria: MatchOperation.IS_IN,
        };
    }
}
